import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback } from 'react';
import { SubmitHandler, useForm, UseFormReturn } from 'react-hook-form';
import { useMutation } from 'react-query';

import { logError } from '@savgroup-front-common/configuration/appInsights/AppInsights';
import { useToasts } from '@savgroup-front-common/core/molecules/NotificationsProvider';
import useStepOrchestratorNextStep from '@savgroup-front-common/core/molecules/StepsOrchestrator/hooks/useStepOrchestratorNextStep';
import { OwnerService } from '@/api';
import { ORDER_BY_REF_AND_PRICE_PAYLOAD_TYPE } from '@/api/Owner/getOrderByOrderReferenceAndOrderPrice';
import { CreateClaimContext, CreateClaimValues } from '@/types';

import messages from '../messages';

import EmailOrderReferenceSchema from './EmailOrderReference.schema';
import { EmailOrderReferenceValues } from './EmailOrderReference.types';

const GetOrderByOrderReferenceAndOrderPrice =
  'getOrderByOrderReferenceAndOrderPrice';

interface UseEmailOrderReferenceProps {
  values: CreateClaimValues;
}

interface UseEmailOrderReferenceReturnValues {
  formContext: UseFormReturn<EmailOrderReferenceValues>;
  isLoading: boolean;
  onSubmit: SubmitHandler<EmailOrderReferenceValues>;
}

const useEmailOrderReference = ({
  values,
}: UseEmailOrderReferenceProps): UseEmailOrderReferenceReturnValues => {
  const { pushErrors } = useToasts({
    extendedMessages: messages,
  });
  const { sellerId } = values;
  const nextStep = useStepOrchestratorNextStep<
    CreateClaimValues,
    CreateClaimContext
  >();

  const formContext = useForm<EmailOrderReferenceValues>({
    resolver: yupResolver(EmailOrderReferenceSchema),
    defaultValues: {
      orderReference: values?.orderReference || '',
      email: values?.email || '',
    },
  });

  const { watch } = formContext;

  const orderReference = watch('orderReference');
  const email = watch('email');

  const { mutateAsync: handleSubmitOrderInformations, isLoading } = useMutation(
    [
      GetOrderByOrderReferenceAndOrderPrice,
      { sellerId, orderReference, email },
    ],
    async () => {
      if (!sellerId || !orderReference || !email) {
        return undefined;
      }

      const response = await OwnerService.getOrderByOrderReferenceAndOrderPrice(
        {
          orderReference,
          email,
          sellerId,
        },
      );

      if (response.failure) {
        pushErrors(response.errors);

        logError(
          `Error with orderReference ${response.errors.map((e) => e.message)}`,
        );

        return undefined;
      }

      return response.value;
    },
  );

  const onSubmit: SubmitHandler<EmailOrderReferenceValues> =
    useCallback(async () => {
      const response = await handleSubmitOrderInformations();

      if (
        response?.importStatus ===
        ORDER_BY_REF_AND_PRICE_PAYLOAD_TYPE.ORDER_ALREADY_IMPORT
      ) {
        window.location = response?.irshUrl as unknown as string & Location;

        return undefined;
      }
      if (
        response?.importStatus ===
        ORDER_BY_REF_AND_PRICE_PAYLOAD_TYPE.CUSTOMER_IDENTIFIED
      ) {
        nextStep({
          ...values,
          email,
          orderReference,
          owner: values.owner ? values.owner : response?.owner,
        });

        return undefined;
      }
      if (
        response?.importStatus ===
        ORDER_BY_REF_AND_PRICE_PAYLOAD_TYPE.ANONYMOUS_CUSTOMER
      ) {
        nextStep({
          ...values,
          email,
          orderReference,
        });

        return undefined;
      }

      return undefined;
    }, [
      handleSubmitOrderInformations,
      orderReference,
      email,
      nextStep,
      values,
    ]);

  return {
    formContext,
    isLoading,
    onSubmit,
  };
};

export default useEmailOrderReference;
